import {
  ref,
  get,
  set,
  update,
  remove,
  query,
  orderByChild,
  onValue,
  Unsubscribe,
} from 'firebase/database';
import { database } from '../config/firebase';
import { SiteCommunication, CommunicationStatus, CommunicationSource } from '../types';
import { checkSubmissionRateLimit, escapeHtml } from '../utils/security';

const LOCAL_STORAGE_PREFIX = 'adiosoft_rtdb_v3_';

export class CommunicationService {
  private isRtdbAvailable(): boolean {
    return Boolean(database);
  }

  private getUtcTimestamp(): number {
    return Date.now();
  }

  private generateId(prefix = 'msg'): string {
    return `${prefix}_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
  }

  private getLocal<T>(key: string, defaultValue: T): T {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_PREFIX + key);
      return raw ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private setLocal<T>(key: string, value: T): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_PREFIX + key, JSON.stringify(value));
    } catch (err) {
      console.warn('Local storage write warning:', err);
    }
  }

  /**
   * Creates a communication record from a public visitor contact form or inquiry.
   * Path: /siteCommunications/{siteId}/{communicationId}
   */
  public async createCommunication(
    siteId: string,
    data: {
      name: string;
      email: string;
      phone?: string;
      subject?: string;
      message: string;
      source?: CommunicationSource;
      sourcePageSlug?: string;
      sourcePageTitle?: string;
      tenantId?: string;
      status?: CommunicationStatus;
      adminNotes?: string;
    }
  ): Promise<SiteCommunication> {
    if (!siteId) {
      throw new Error('Geçersiz siteId.');
    }

    // Rate limit check: max 5 messages per 60 seconds per site from same client
    const isAllowed = checkSubmissionRateLimit(`comm_${siteId}`, 5, 60000);
    if (!isAllowed) {
      throw new Error('Çok fazla mesaj denemesi yaptınız. Lütfen bir süre sonra tekrar deneyin.');
    }

    const now = this.getUtcTimestamp();
    const id = this.generateId('msg');

    const sanitizedName = escapeHtml(data.name.trim());
    const sanitizedEmail = escapeHtml(data.email.trim());
    const sanitizedPhone = data.phone ? escapeHtml(data.phone.trim()) : undefined;
    const sanitizedSubject = data.subject ? escapeHtml(data.subject.trim()) : 'Web Sitesi İletişim Formu';
    const sanitizedMessage = escapeHtml(data.message.trim());

    if (!sanitizedName || !sanitizedEmail || !sanitizedMessage) {
      throw new Error('Lütfen ad, e-posta ve mesaj alanlarını eksiksiz doldurun.');
    }

    const newCommunication: SiteCommunication = {
      id,
      siteId,
      tenantId: data.tenantId,
      name: sanitizedName,
      email: sanitizedEmail,
      phone: sanitizedPhone,
      subject: sanitizedSubject,
      message: sanitizedMessage,
      source: data.source || 'contact_form',
      sourcePageSlug: data.sourcePageSlug,
      sourcePageTitle: data.sourcePageTitle,
      status: data.status || 'NEW',
      isDeleted: false,
      adminNotes: data.adminNotes,
      createdAt: now,
      updatedAt: now,
    };

    if (this.isRtdbAvailable() && database) {
      try {
        await set(ref(database, `siteCommunications/${siteId}/${id}`), newCommunication);
      } catch (err) {
        console.warn('Firebase RTDB communication write fallback:', err);
      }
    }

    // Update local store for offline/instant UI feedback
    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (!allComm[siteId]) allComm[siteId] = [];
    allComm[siteId].unshift(newCommunication);
    this.setLocal('siteCommunications', allComm);

    // Broadcast local event for immediate reactivity across components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('adiosoft_communication_updated', {
          detail: { siteId, communication: newCommunication },
        })
      );
    }

    return newCommunication;
  }

  /**
   * Retrieves all communications for a specific site, sorted by newest first.
   */
  public async getCommunications(siteId: string): Promise<SiteCommunication[]> {
    if (!siteId) return [];

    if (this.isRtdbAvailable() && database) {
      try {
        const commQuery = query(ref(database, `siteCommunications/${siteId}`), orderByChild('createdAt'));
        const snap = await get(commQuery);
        if (snap.exists()) {
          const raw = snap.val() as Record<string, SiteCommunication>;
          const list = Object.entries(raw).map(([key, val]) => ({
            ...val,
            id: val?.id || key,
          })).filter((c) => !c.isDeleted);
          const sorted = list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

          // Sync local storage
          const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
          allComm[siteId] = sorted;
          this.setLocal('siteCommunications', allComm);

          return sorted;
        }
      } catch (err) {
        console.warn('RTDB getCommunications fallback:', err);
      }
    }

    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    return (allComm[siteId] || []).filter((c) => !c.isDeleted).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  /**
   * Retrieves a single communication.
   */
  public async getCommunication(siteId: string, communicationId: string): Promise<SiteCommunication | null> {
    if (!siteId || !communicationId) return null;

    if (this.isRtdbAvailable() && database) {
      try {
        const snap = await get(ref(database, `siteCommunications/${siteId}/${communicationId}`));
        if (snap.exists()) {
          const comm = snap.val() as SiteCommunication;
          if (!comm.isDeleted) return comm;
        }
      } catch (err) {
        console.warn('RTDB getCommunication fallback:', err);
      }
    }

    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    return (allComm[siteId] || []).find((c) => c.id === communicationId && !c.isDeleted) || null;
  }

  /**
   * Updates communication status (e.g., NEW -> READ -> ARCHIVED) with optional admin notes.
   */
  public async updateCommunicationStatus(
    siteId: string,
    communicationId: string,
    status: CommunicationStatus,
    adminNotes?: string
  ): Promise<void> {
    if (!siteId || !communicationId) return;
    const now = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteCommunications/${siteId}/${communicationId}`), {
          status,
          adminNotes: adminNotes !== undefined ? adminNotes : null,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB updateCommunicationStatus fallback:', err);
      }
    }

    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (allComm[siteId]) {
      const target = allComm[siteId].find((c) => c.id === communicationId);
      if (target) {
        target.status = status;
        if (adminNotes !== undefined) target.adminNotes = adminNotes;
        target.updatedAt = now;
        this.setLocal('siteCommunications', allComm);
      }
    }
  }

  /**
   * Marks a communication as read.
   */
  public async markAsRead(siteId: string, communicationId: string): Promise<void> {
    return this.updateCommunicationStatus(siteId, communicationId, 'READ');
  }

  /**
   * Archives a communication.
   */
  public async archiveCommunication(siteId: string, communicationId: string): Promise<void> {
    return this.updateCommunicationStatus(siteId, communicationId, 'ARCHIVED');
  }

  /**
   * Soft-deletes a communication.
   */
  public async deleteCommunication(siteId: string, communicationId: string): Promise<void> {
    if (!siteId || !communicationId) return;
    const now = this.getUtcTimestamp();

    if (this.isRtdbAvailable() && database) {
      try {
        await update(ref(database, `siteCommunications/${siteId}/${communicationId}`), {
          isDeleted: true,
          updatedAt: now,
        });
      } catch (err) {
        console.warn('RTDB deleteCommunication fallback:', err);
      }
    }

    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    if (allComm[siteId]) {
      allComm[siteId] = allComm[siteId].filter((c) => c.id !== communicationId);
      this.setLocal('siteCommunications', allComm);
    }
  }

  /**
   * Computes unread count for a site.
   */
  public async getUnreadCount(siteId: string): Promise<number> {
    const list = await this.getCommunications(siteId);
    return list.filter((c) => c.status === 'NEW' && !c.isDeleted).length;
  }

  /**
   * Realtime subscription to /siteCommunications/{siteId}
   * Returns an unsubscribe function.
   */
  public subscribeToSiteCommunications(
    siteId: string,
    callback: (communications: SiteCommunication[]) => void
  ): () => void {
    if (!siteId) return () => {};

    // Local event listener handler for instant UI updates across components
    const handleLocalUpdate = (e: Event) => {
      const customEv = e as CustomEvent;
      if (customEv.detail?.siteId === siteId) {
        this.getCommunications(siteId).then((comms) => {
          callback(comms);
        });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('adiosoft_communication_updated', handleLocalUpdate);
    }

    let rtdbUnsub: (() => void) | null = null;

    if (this.isRtdbAvailable() && database) {
      try {
        const commRef = ref(database, `siteCommunications/${siteId}`);
        rtdbUnsub = onValue(
          commRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const raw = snapshot.val() as Record<string, SiteCommunication>;
              const list = Object.entries(raw).map(([key, val]) => ({
                ...val,
                id: val?.id || key,
              })).filter((c) => !c.isDeleted);
              const sorted = list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

              // Update local mirror
              const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
              allComm[siteId] = sorted;
              this.setLocal('siteCommunications', allComm);

              callback(sorted);
            } else {
              callback([]);
            }
          },
          (error) => {
            console.warn('Realtime subscription error for site communications:', error);
            // Fallback to local storage
            const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
            callback((allComm[siteId] || []).filter((c) => !c.isDeleted));
          }
        );
      } catch (err) {
        console.warn('Failed to attach realtime listener to site communications:', err);
      }
    }

    // Initial load from local storage
    const allComm = this.getLocal<Record<string, SiteCommunication[]>>('siteCommunications', {});
    callback((allComm[siteId] || []).filter((c) => !c.isDeleted));

    return () => {
      if (rtdbUnsub) rtdbUnsub();
      if (typeof window !== 'undefined') {
        window.removeEventListener('adiosoft_communication_updated', handleLocalUpdate);
      }
    };
  }
}

export const communicationService = new CommunicationService();
export default communicationService;
